import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useCallback, useMemo, useState } from 'react';

import { DragOverlayCell } from './DragOverlayCell';
import { SortableHeaderCell } from './SortableHeaderCell';
import { StructureTableHeadProps } from './StructureTableHead.props';

const STICKY_CELL_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  cursor: 'default',
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
  width: 70,
  position: 'sticky' as const,
  right: 0,
  bgcolor: 'v4.tableEven',
  zIndex: 2,
} as const;

export const StructureTableHead: React.FC<StructureTableHeadProps> = ({
  columns,
  sortModel,
  onSort,
  onColumnReorder,
  onColumnResize,
  elasticLastColumn = false,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const columnIds = useMemo(() => columns.map((c) => c.key), [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  }, []);

  const handleDragOver = useCallback((e: DragOverEvent) => {
    setOverId(e.over ? String(e.over.id) : null);
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      setActiveId(null);
      setOverId(null);
      const { active, over } = e;
      if (!over || active.id === over.id || !onColumnReorder) return;
      const oldIndex = columns.findIndex((c) => c.key === active.id);
      const newIndex = columns.findIndex((c) => c.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) onColumnReorder(oldIndex, newIndex);
    },
    [columns, onColumnReorder]
  );

  const activeCol = activeId ? columns.find((c) => c.key === activeId) : null;

  const dropSide = useMemo<'left' | 'right' | null>(() => {
    if (!activeId || !overId || activeId === overId) return null;
    const activeIdx = columns.findIndex((c) => c.key === activeId);
    const overIdx = columns.findIndex((c) => c.key === overId);
    return activeIdx < overIdx ? 'right' : 'left';
  }, [activeId, overId, columns]);

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: 'v4.tableEven' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          accessibility={{ container: document.body }}
        >
          <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
            {columns.map((col, colIndex) => (
              <SortableHeaderCell
                key={col.key}
                col={col}
                sortModel={sortModel}
                onSort={onSort}
                dropIndicator={col.key === overId ? dropSide : null}
                onColumnResize={
                  elasticLastColumn && colIndex === columns.length - 1 ? undefined : onColumnResize
                }
              />
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeCol ? <DragOverlayCell label={activeCol.label} /> : null}
          </DragOverlay>
        </DndContext>
        <TableCell sx={STICKY_CELL_SX} aria-label="Actions" />
      </TableRow>
    </TableHead>
  );
};
